import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import Lozenge from '@atlaskit/lozenge/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { type SearchCompassPartial } from '@atlassian/dynamic-cards-search-client';
import { highlightMatchedText } from '@atlassian/dynamic-cards-search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { MetaDataItemSkeletonWide } from '../../../../../../common/ui/search-result-skeleton';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { truncateTextWithQuery } from '../../../../../../common/utils/truncate-text-with-query';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../controllers/store';
import {
	useIsCompassTeamsLoading,
	useSearchQuery,
} from '../../../../../../controllers/store/search';
import { messages } from './messages';

interface CompassSearchResultProps {
	partial: SearchCompassPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

const dotStyles = xcss({
	width: '4px',
	height: '4px',
	borderRadius: 'radius.xsmall',
	marginLeft: 'space.050',
});

const pendingStyles = xcss({
	backgroundColor: 'color.background.accent.yellow.subtle',
});
const activeStyles = xcss({
	backgroundColor: 'color.background.accent.green.subtle',
});
const archivedStyles = xcss({
	backgroundColor: 'color.background.accent.gray.subtle',
});

const getComponentStateIcon = (state: string) => {
	switch (state) {
		case 'PENDING':
			return <Box xcss={[dotStyles, pendingStyles]}></Box>;
		case 'ACTIVE':
			return <Box xcss={[dotStyles, activeStyles]}></Box>;
		case 'ARCHIVED':
			return <Box xcss={[dotStyles, archivedStyles]}></Box>;
	}
};

const owningTeamStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
});

export const CompassSearchResult = ({
	partial,
	position,
}: CompassSearchResultProps): React.JSX.Element => {
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();
	const { formatMessage } = useIntl();
	const isCompassTeamsLoading = useIsCompassTeamsLoading();
	const [searchQuery] = useSearchQuery();

	const { title, url, tier, component, lastModifiedDate, type, ownerName } = partial;
	const { state, description } = component || {};

	const getLastModified = useMemo(() => {
		if (!lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={lastModifiedDate} />;
	}, [lastModifiedDate]);

	const getComponentState = useMemo(() => {
		if (!state) {
			return null;
		}

		return (
			<Lozenge appearance={'default'}>
				<Inline alignBlock="center" space="space.075">
					{getComponentStateIcon(state)}
					{formatMessage(messages.state, { state })}
				</Inline>
			</Lozenge>
		);
	}, [state, formatMessage]);

	const getTier = useMemo(() => {
		if (!tier) {
			return null;
		}

		return <Lozenge appearance={'default'}>{formatMessage(messages.tier, { tier })}</Lozenge>;
	}, [tier, formatMessage]);

	const getOwnerName = useMemo(() => {
		if (ownerName) {
			return <Box xcss={owningTeamStyles}>{formatMessage(messages.ownerName, { ownerName })}</Box>;
		}

		if (isCompassTeamsLoading) {
			return <MetaDataItemSkeletonWide />;
		}
	}, [ownerName, formatMessage, isCompassTeamsLoading]);

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return title;
		}
		return enrichExcerpt(highlightedTitle);
	}, [title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = truncateTextWithQuery(description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	return (
		<SearchResult
			{...partial}
			primaryProduct={primaryProduct}
			url={url}
			icon={<ProductIcon product={ProductKeys.Compass} />}
			title={enrichedTitle}
			headerMetadata={
				<SearchMetadata
					product="compass"
					productSelected={!!selectedProduct}
					parts={[
						['type', <SearchResultType type={type} />],
						['ownerName', getOwnerName],
						['lastModified', getLastModified],
						['componentState', getComponentState],
						['tier', getTier],
					]}
				/>
			}
			description={enrichedDescription}
			cloudId={cloudId}
			position={position}
		/>
	);
};
